<template>
  <div class="card">
    <div class="card-header">
      <h3 class="card-title">
        Choose Membership Plan
      </h3>
    </div>
    <hr class="card-title-line">
    <div class="card-content">
      <div
        v-if="gourmetUsageQqqConfig"
        class="operator-script-section mb-20"
        :class="{ 'has-trial': gourmetUsageQqqConfig.hasTrial }"
      >
        <div class="script-header">
          <span
            class="script-badge"
            :class="{ 'trial': gourmetUsageQqqConfig.hasTrial, 'no-trial': !gourmetUsageQqqConfig.hasTrial }"
          >
            {{ gourmetUsageQqqConfig.title }}
          </span>
        </div>
        <div class="script-content">
          <p
            class="script-text"
            v-html="gourmetUsageQqqConfig.scriptHtml"
          />
        </div>
      </div>
      <p
        v-if="isTrialDisclaimer"
        class="notification-message mb-20 mt-10"
      >
        {{ trialRenderConfig?.registration.text }}
        <el-checkbox
          v-if="operatorCanSkipFreeTrial"
          v-model="isOptOutOfFreeTrial"
          label="The client prefers to skip the free membership and subscribe immediately."
        >
          The client prefers to skip the free membership and subscribe immediately.
        </el-checkbox>
      </p>
      <price-planning-table
        :is-operator-registration="true"
        @choose-membership="chooseMembership"
        @choose-annual-type="chooseAnnualType"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, defineAsyncComponent, computed } from 'vue';
import { ElLoading } from 'element-plus';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { optOutOfFreeTrial } from '@/services/abTests/optOutOfFreeTrial';
import { useProfile } from '@/use/profile';
import { gourmetUsageQqqGroup, trialRenderConfig } from '@/services/abTests/abTestsViewModel';
import { operatorsAllowedToSkipFreeTrial } from '@/constants/operator';
import { callerService } from '@/services/caller';
import { planCurrency, planPricing } from '@/services/pricing/pricingViewModel'

const PricePlanningTable = defineAsyncComponent(() => import('@/components/UIComponents/PricePlanning.vue') as any);

const emit = defineEmits(['goto-next-step']);

const porps = defineProps<{
  isGourmetRegistration?: boolean;
}>()

const { newCustomerInfo, setNewCustomerInfo } = useCustomer();
const { showSuccessPopupDialog, showErrorPopupDialog, showConfirmPopupDialog } = useSwalDialog();
const { currentUser } = useProfile();

const isAnnuallyType = ref(false);

const gourmetUsageQqqConfig = computed(() => {
  const valuePricing = planPricing.value.find(pp => pp.annual?.planKind === 'valueAnnual' || pp.monthly?.planKind === 'value');
  const valueAnnualMonthlyPrice = valuePricing?.annualPerMonthPrice?.[planCurrency.value];
  const valueMonthlyPrice = valuePricing?.monthly?.price[planCurrency.value];
  const basicPricing = planPricing.value.find(pp => pp.annual?.planKind === 'basicAnnual' || pp.monthly?.planKind === 'basic');
  const basicMonthlyPrice = basicPricing?.monthly?.price[planCurrency.value];
  if (!porps.isGourmetRegistration || !gourmetUsageQqqGroup.value) {
    return null;
  }

  if (
    gourmetUsageQqqGroup.value === 'control'
    && trialRenderConfig.value?.isEligible
    && trialRenderConfig.value?.isGourmet7DayAnnual
    && valueAnnualMonthlyPrice
    && valueMonthlyPrice
  ) {
    return {
      title: 'Trials on Annual',
      hasTrial: true,
      scriptHtml: [
        `"Our most popular plan is the <strong>Value plan</strong> which has the lowest order fees.`,
        `The annual option is the best deal, with a <strong>7 day free trial</strong>`,
        `and a membership fee of <strong>$${valueAnnualMonthlyPrice} per month</strong> billed upfront.`,
        `We also offer a monthly option which is <strong>$${valueMonthlyPrice}</strong> billed monthly.`,
        `Would you prefer our monthly plan or a free trial of the annual plan?""`,
      ].join(' '),
    };
  }

  if (
    gourmetUsageQqqGroup.value === 'test'
    && trialRenderConfig.value?.isEligible
    && trialRenderConfig.value?.isGourmet7Day
    && valueMonthlyPrice
  ) {
    return {
      title: 'Trials on Monthly & Annual',
      hasTrial: true,
      scriptHtml: [
        `"Since it sounds like you'll be using us regularly,`,
        `we can start you with a <u><strong>free 7-day trial</strong></u>`,
        `of our <strong>Value Plan</strong>, which has the lowest order fees."`,
        `<br><br>"Today you'd just pay for the food and concierge fee."`,
        `<br><br>"After the week, if it's a good fit,`,
        `it becomes our <strong>$${valueMonthlyPrice} monthly plan</strong> — and you can cancel anytime."`,
        `<br><br>"Can I get your free trial started today?"`,
      ].join(' '),
    };
  }

  if (
    gourmetUsageQqqGroup.value === 'test'
    && valueMonthlyPrice
    && basicMonthlyPrice
  ) {
    return {
      title: 'No Free Trial',
      hasTrial: false,
      scriptHtml: [
        `"Our most popular membership is the <strong>Value Plan</strong>`,
        `which gives you the lowest order fees. This costs <strong>$${valueMonthlyPrice} per month</strong>`,
        `with <strong>$4 per order</strong> and is the best deal if you order twice.`,
        `"<br><br>"We also offer a <strong>Basic Plan</strong> which costs`,
        `<strong>$${basicMonthlyPrice} per month</strong> with <strong>$8.50 per order</strong>."`,
        `<br><br>"Would you prefer the Value or the Basic plan today?"`,
      ].join(' '),
    };
  }

  return null;
});

const isTrialDisclaimer = computed(() => {
  const { isEligible, isAnnual, isMonthly } = trialRenderConfig.value || {};
  const periodMatch = isAnnuallyType.value ? isAnnual : isMonthly;
  return isEligible && periodMatch;
});

const isOptOutOfFreeTrial = ref(false);

const operatorCanSkipFreeTrial = computed(() => operatorsAllowedToSkipFreeTrial.includes(currentUser.value.id));

const chooseMembership = async (plan: string, planName: string) => {
  const annualPlanConfirmHtml = `
    <ul class="text-left">
      <li class="mb-3">Please confirm that this is the plan the user wants!</li>
    </ul>
  `;
  const monthlyPlanConfirmHtml = 'Please confirm that this is the plan the user wants!';

  const isPlanConfirmed = await showConfirmPopupDialog({
    text: isAnnuallyType.value ? annualPlanConfirmHtml : monthlyPlanConfirmHtml,
    title: planName,
    type: 'html',
  });

  if (!isPlanConfirmed) {
    return;
  }

  const loading = ElLoading.service({
    lock: true,
    text: 'Updating membership plan...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  try {
    if (trialRenderConfig.value?.isEligible && isOptOutOfFreeTrial.value && operatorCanSkipFreeTrial.value) {
      const optedOut = await optOutOfFreeTrial(newCustomerInfo.value.id);
      if (!optedOut) {
        loading.close();
        await showErrorPopupDialog({
          text: 'Could not remove caller from the trial!',
        });
        return false;
      }
    }

    await callerService.updateCaller({
      callerId: newCustomerInfo.value.id,
      data: {
        registerPlan: plan,
      },
    });

    loading.close();
    await showSuccessPopupDialog({
      text: 'The membership has been updated successfully!',
    });

    setNewCustomerInfo({
      ...newCustomerInfo.value,
      registerPlan: plan,
    });

    emit('goto-next-step');
  } catch (error) {
    loading.close();

    const errorText = util.getErrorResponse(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
};

const chooseAnnualType = (value: boolean) => {
  isAnnuallyType.value = value;
};
</script>

<style lang="scss" scoped>
.operator-script-section {
  .script-header {
    margin-bottom: 12px;

    .script-badge {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;

      &.trial {
        background: #4CAF50;
        color: #fff;
      }

      &.no-trial {
        background: #f39c12;
        color: #fff;
      }
    }
  }

  .script-content {
    background: #fffaf0;
    border: 1px solid #f5e6d3;
    border-radius: 8px;
    padding: 16px 20px;

    .script-text {
      font-size: 14px;
      color: #555;
      line-height: 1.8;
      font-style: italic;
      margin: 0;

      :deep(strong) {
        font-style: normal;
        color: #333;
      }

      :deep(u) {
        text-decoration: underline;
      }
    }
  }

  &.has-trial {
    .script-content {
      background: #f0fff4;
      border-color: #4CAF50;
    }
  }
}
</style>
